import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AIReasoningLogs = ({ logs, currentLanguage }) => {
  const [expandedLog, setExpandedLog] = useState(null);

  const translations = {
    en: {
      title: 'AI Reasoning Logs',
      noLogs: 'No reasoning logs available',
      viewDetails: 'View Details',
      hideDetails: 'Hide Details',
      reasoning: 'AI Reasoning',
      matchScore: 'Match Score',
      candidates: 'Candidates Evaluated',
      selectedTraveler: 'Selected Traveler'
    },
    ko: {
      title: 'AI 추론 로그',
      noLogs: '추론 로그가 없습니다',
      viewDetails: '세부정보 보기',
      hideDetails: '세부정보 숨기기',
      reasoning: 'AI 추론',
      matchScore: '매칭 점수',
      candidates: '평가된 후보자',
      selectedTraveler: '선택된 여행자'
    }
  };

  const t = translations?.[currentLanguage];

  const toggleExpand = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-success';
    if (score >= 0.6) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Brain" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">{t?.title}</h2>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {logs?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t?.noLogs}</p>
          </div>
        ) : (
          logs?.map((log) => {
            const isExpanded = expandedLog === log?.id;

            return (
              <div
                key={log?.id}
                className="border border-border rounded-lg p-3 hover:bg-accent/5 transition-base"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Sparkles" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Request #{log?.requestId?.slice(0, 8)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(log?.createdAt)?.toLocaleString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleExpand(log?.id)}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    {isExpanded ? t?.hideDetails : t?.viewDetails}
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <p className="text-xs text-muted-foreground">{t?.matchScore}</p>
                    <p className={`text-sm font-bold ${getScoreColor(log?.matchScore)}`}>
                      {(log?.matchScore * 100)?.toFixed(0)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t?.candidates}</p>
                    <p className="text-sm font-bold text-foreground">{log?.candidatesCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{currentLanguage === 'en' ? 'Mode' : '모드'}</p>
                    <p className="text-sm font-bold text-foreground">{log?.matchingMode}</p>
                  </div>
                </div>

                {isExpanded && log?.aiReasoning && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="space-y-2">
                      {log?.selectedTravelerId && (
                        <div className="p-2 bg-primary/10 rounded">
                          <p className="text-xs font-medium text-primary mb-1">{t?.selectedTraveler}</p>
                          <p className="text-xs text-foreground font-mono">
                            {log?.selectedTravelerId?.slice(0, 16)}...
                          </p>
                        </div>
                      )}

                      <div className="p-2 bg-muted/30 rounded">
                        <p className="text-xs font-medium text-foreground mb-1">{t?.reasoning}</p>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {typeof log?.aiReasoning === 'object' ? (
                            <pre className="whitespace-pre-wrap font-mono text-xs">
                              {JSON.stringify(log?.aiReasoning, null, 2)}
                            </pre>
                          ) : (
                            <p>{log?.aiReasoning}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Clock" size={12} />
                        <span>
                          {currentLanguage === 'en' ? 'Execution Time' : '실행 시간'}: {log?.executionTimeMs}ms
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AIReasoningLogs;